/**
 * CLE-2811 — Queued → Downloading → Reading pages → AI modeling → Saving. A finished phase shows how long
 * it took (when that is known — see `derivePhaseDurations`), the active one breathes and counts up, and
 * the rest wait, muted. Horizontal; stacked on a narrow screen.
 */

import React from "react";
import styled, { css } from "styled-components";

import { PHASE_SKIPPED_REUSED } from "../copy";
import { formatCounter } from "../utils";
import { TIMELINE_PHASES, TimelinePhase, phaseIndex } from "./progressModel";
import { GREEN, pulse } from "./styles";

type StepState = "done" | "current" | "todo";

const NARROW = "@media (max-width: 640px)";

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;

  ${NARROW} {
    flex-direction: column;
    gap: 0.6rem;
  }
`;

const lineColor = (state: StepState) => {
  if (state === "done") return GREEN;
  if (state === "current") return "var(--primary-color)";
  return "var(--surface-border)";
};

const Step = styled.li<{ $state: StepState }>`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.3rem;
  padding: 0 0.15rem;

  /* The connector INTO this step, drawn from the previous dot's centre to this one's. */
  &:not(:first-child)::before {
    content: "";
    position: absolute;
    top: 0.6rem;
    right: 50%;
    width: 100%;
    height: 2px;
    background: ${({ $state }) => lineColor($state)};
  }

  ${NARROW} {
    flex-direction: row;
    align-items: center;
    text-align: left;
    gap: 0.6rem;
    padding: 0;

    &:not(:first-child)::before {
      display: none;
    }
  }
`;

const Dot = styled.span<{ $state: StepState }>`
  position: relative;
  z-index: 1;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  color: #ffffff;
  background: var(--surface-card, #ffffff);
  border: 2px solid var(--surface-border);

  ${({ $state }) =>
    $state === "done" &&
    css`
      background: ${GREEN};
      border-color: ${GREEN};
    `}

  ${({ $state }) =>
    $state === "current" &&
    css`
      border-color: var(--primary-color);
      animation: ${pulse} 1.8s ease-out infinite;

      &::after {
        content: "";
        width: 0.45rem;
        height: 0.45rem;
        border-radius: 50%;
        background: var(--primary-color);
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

const Label = styled.span<{ $state: StepState }>`
  display: block;
  font-size: 0.8rem;
  line-height: 1.25;
  font-weight: ${({ $state }) => ($state === "current" ? 600 : 400)};
  color: ${({ $state }) =>
    $state === "current" ? "var(--primary-color)" : "var(--text-color)"};
  opacity: ${({ $state }) => ($state === "todo" ? 0.5 : 1)};
`;

const Caption = styled.span`
  display: block;
  min-height: 1em;
  font-size: 0.72rem;
  color: var(--text-color-secondary);
  font-variant-numeric: tabular-nums;
`;

interface IPhaseTimelineProps {
  active: TimelinePhase;
  /** How long each finished phase took, in ms; null when unknown. */
  durations: Record<TimelinePhase, number | null>;
  /** How long the active phase has been going, or null when unknown. */
  activeElapsedMs: number | null;
  /** The previous pass's text was reused: Downloading was skipped. */
  reused: boolean;
}

function PhaseTimeline({
  active,
  durations,
  activeElapsedMs,
  reused,
}: IPhaseTimelineProps) {
  const activeAt = phaseIndex(active);
  return (
    <Steps aria-label="Generation phases">
      {TIMELINE_PHASES.map((phase, i) => {
        let state: StepState = "todo";
        if (i < activeAt) state = "done";
        else if (i === activeAt) state = "current";

        let caption = "";
        if (state === "done") {
          const ms = durations[phase.id];
          if (phase.id === "downloading" && reused && ms == null)
            caption = PHASE_SKIPPED_REUSED;
          else if (ms != null) caption = formatCounter(ms);
        } else if (state === "current" && activeElapsedMs != null) {
          caption = formatCounter(activeElapsedMs);
        }

        return (
          <Step
            key={phase.id}
            $state={state}
            aria-current={state === "current" ? "step" : undefined}
          >
            <Dot $state={state}>
              {state === "done" ? (
                <i className="pi pi-check" style={{ fontSize: "0.6rem" }} />
              ) : null}
            </Dot>
            <span>
              <Label $state={state}>{phase.label}</Label>
              <Caption>{caption}</Caption>
            </span>
          </Step>
        );
      })}
    </Steps>
  );
}

export default PhaseTimeline;
